import BaseEvalConfig, {
  EvalFormValues,
  baseFormSchema,
} from './base-eval-config';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
} from '@/components/ui/form';
import { UseFormReturn } from 'react-hook-form';
import HelpTooltip from '@/components/ui/help-tooltip';
import type { Eval } from '@/types/evals-types';
import { useEffect } from 'react';
import { useNotification } from '@/components/notifications';
import { useEvalTypeMetadata } from '@/hooks/queries/evaluation/use-get-eval-types';
import { EvalModel } from '@/types/evals-types';
import { autoGenerateScoreEvalConfig } from '@/services/evaluation/auto-generate-score-eval-config';
import { z } from '@/lib/zod-config';

interface ScoreEvalConfigProps {
  evalData: Eval;
  onSave: (data: Partial<Eval>) => void;
  onDuplicate: () => void;
  onDelete?: (evalId: string) => void;
}

// Form validation schema for score evaluation
const scoreEvalFormSchema = baseFormSchema.extend({
  eval_config: z.object({
    min_score: z.string().trim().min(1, 'Minimum score is required'),
    max_score: z.string().trim().min(1, 'Maximum score is required'),
    score_criteria: z.record(
      z.string(),
      z.string().trim().min(1, 'Score criteria description is required'),
    ),
    additional_instructions: z.string().nullable().optional(),
  }),
});

export default function ScoreEvalConfig({
  evalData,
  onSave,
  onDuplicate,
  onDelete,
}: ScoreEvalConfigProps) {
  const notification = useNotification();
  const metadata = useEvalTypeMetadata('score');

  // Helper to create scoreCriteria object from string or object
  function getScoreCriteriaObject(
    criteria: string | { [score: string]: string },
    min: string,
    max: string,
  ): { [score: string]: string } {
    const minNum = parseInt(min, 10);
    const maxNum = parseInt(max, 10);
    const obj: { [score: string]: string } = {};
    if (typeof criteria === 'object' && criteria !== null) {
      // Fill missing keys with empty string
      for (let i = minNum; i <= maxNum; i++) {
        obj[i] = criteria[i] || '';
      }
      return obj;
    }
    // Try to parse numbered lines from string
    const lines = (criteria || '').split(/\n|\r/);
    for (let i = minNum; i <= maxNum; i++) {
      const line = lines.find((l) => l.trim().startsWith(i + ':'));
      obj[i] = line ? line.split(':').slice(1).join(':').trim() : '';
    }
    return obj;
  }

  // Default values for the form
  const minScore =
    evalData?.eval_config && 'min_score' in evalData.eval_config
      ? evalData.eval_config.min_score.toString() // has to convert to string because backend store it as number
      : '1';
  const maxScore =
    evalData?.eval_config && 'max_score' in evalData.eval_config
      ? evalData.eval_config.max_score.toString() // has to convert to string because backend store it as number
      : '10';
  const defaultValues: EvalFormValues = {
    name: evalData?.name || '',
    description: evalData?.description || '',
    model:
      evalData?.model === EvalModel.GPT_4O
        ? EvalModel.GPT_4O_MINI
        : evalData?.model || EvalModel.CLAUDE_SONNET_46,
    eval_config: {
      min_score: minScore,
      max_score: maxScore,
      score_criteria:
        evalData?.eval_config && 'score_criteria' in evalData.eval_config
          ? getScoreCriteriaObject(
              evalData.eval_config.score_criteria,
              minScore,
              maxScore,
            )
          : getScoreCriteriaObject('', minScore, maxScore),
      additional_instructions:
        evalData?.eval_config &&
        'additional_instructions' in evalData.eval_config
          ? (evalData.eval_config.additional_instructions ?? '')
          : '',
    },
  };

  // Handler for generating score criteria from description
  const handleGenerateFromDescription = async (
    form: UseFormReturn<EvalFormValues>,
  ) => {
    const description = form.getValues('description');
    const model = form.getValues('model');
    const minScore = parseInt(
      form.getValues('eval_config.min_score') || '1',
      10,
    );
    const maxScore = parseInt(
      form.getValues('eval_config.max_score') || '10',
      10,
    );

    if (!description) {
      notification.error('Please provide a description first');
      return;
    }

    if (maxScore > 10) {
      notification.error('Maximum score cannot be greater than 10');
      return;
    }

    try {
      const response = await autoGenerateScoreEvalConfig({
        model,
        eval_desc: description,
        min_score: minScore,
        max_score: maxScore,
      });

      form.setValue('eval_config.score_criteria', response.scoring_criteria);
      notification.success('Generated scoring criteria successfully');
    } catch (error) {
      notification.error('Failed to generate scoring criteria');
      console.error('Error generating criteria:', error);
    }
  };

  // Transform form data for saving
  const onSubmitTransform = (data: EvalFormValues): Partial<Eval> => {
    const maxScore =
      'max_score' in data.eval_config ? data.eval_config.max_score : undefined;
    const maxScoreNum = parseInt(maxScore || '10', 10);
    const baseConfig: Partial<Eval> = {
      ...data,
      type: 'score',
      eval_config: {
        ...data.eval_config,
        additional_instructions:
          data.eval_config && 'additional_instructions' in data.eval_config
            ? data.eval_config.additional_instructions === ''
              ? null
              : data.eval_config.additional_instructions
            : undefined,
      },
    };
    if (maxScoreNum > 10) {
      notification.error('Maximum score cannot be greater than 10.');
      return {
        ...baseConfig,
        eval_config: {
          ...baseConfig.eval_config!,
          max_score: '10',
        },
      };
    }
    return baseConfig;
  };

  // Render type-specific fields
  function RenderFields({
    form,
    isEditing,
  }: {
    form: UseFormReturn<EvalFormValues>;
    isEditing: boolean;
  }) {
    const minScore = form.watch('eval_config.min_score');
    const maxScore = form.watch('eval_config.max_score');

    // Watch for minScore/maxScore changes and update scoreCriteria keys accordingly
    useEffect(() => {
      const min = parseInt(form.getValues('eval_config.min_score') || '1', 10);
      const max = parseInt(form.getValues('eval_config.max_score') || '10', 10);
      const criteria = form.getValues('eval_config.score_criteria') || {};
      let changed = false;
      const updated: { [score: string]: string } = {};
      for (let i = min; i <= max; i++) {
        const key = i.toString();
        updated[key] = criteria[key] || '';
        if (!(key in criteria)) changed = true;
      }
      // Remove keys outside the range
      Object.keys(criteria).forEach((k) => {
        if (parseInt(k, 10) < min || parseInt(k, 10) > max) changed = true;
      });
      if (changed) {
        form.setValue('eval_config.score_criteria', updated);
      }
    }, [minScore, maxScore, form]);

    return (
      <>
        <div className='flex gap-4'>
          <FormField
            control={form.control}
            name='eval_config.min_score'
            render={({ field }) => (
              <FormItem className='flex-1'>
                <div className='flex items-center gap-2'>
                  <FormLabel className='label-required'>
                    Minimum Score
                  </FormLabel>
                  <HelpTooltip label='Minimum score help'>
                    Set the lowest possible score for this evaluation.
                  </HelpTooltip>
                </div>
                <FormControl>
                  <Input
                    type='number'
                    min='1'
                    max={maxScore ?? '10'}
                    disabled={!isEditing}
                    {...field}
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name='eval_config.max_score'
            render={({ field }) => (
              <FormItem className='flex-1'>
                <div className='flex items-center gap-2'>
                  <FormLabel className='label-required'>
                    Maximum Score
                  </FormLabel>
                  <HelpTooltip label='Maximum score help'>
                    Set the highest possible score for this evaluation.
                  </HelpTooltip>
                </div>
                <FormControl>
                  <Input
                    type='number'
                    min='1'
                    max='10'
                    disabled={!isEditing}
                    {...field}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>
        <FormField
          control={form.control}
          name='eval_config.score_criteria'
          render={({ field }) => {
            const min = parseInt(
              form.getValues('eval_config.min_score') || '1',
              10,
            );
            const max = parseInt(
              form.getValues('eval_config.max_score') || '10',
              10,
            );
            const criteria = field.value || {};
            return (
              <FormItem>
                <div className='flex items-center gap-2'>
                  <FormLabel className='label-required'>
                    Scoring Criteria
                  </FormLabel>
                  <HelpTooltip label='Scoring criteria help'>
                    Describe what each possible score means in this evaluation.
                  </HelpTooltip>
                </div>
                <div className='overflow-x-auto'>
                  <table className='min-w-full border border-muted rounded'>
                    <thead>
                      <tr>
                        <th className='px-2 py-1 border-b text-left'>Score</th>
                        <th className='px-2 py-1 border-b text-left'>
                          Criteria
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {Array.from({ length: max - min + 1 }, (_, i) => {
                        const score = (min + i).toString();
                        return (
                          <tr key={score}>
                            <td className='px-2 py-1 border-b w-16 font-semibold'>
                              {score}
                            </td>
                            <td className='px-2 py-1 border-b'>
                              <Textarea
                                value={criteria[score] || ''}
                                onChange={(e) => {
                                  const updated = {
                                    ...criteria,
                                    [score]: e.target.value,
                                  };
                                  field.onChange(updated);
                                }}
                                placeholder={`e.g. what a ${score} looks like`}
                                className='min-h-[60px] w-full'
                                disabled={!isEditing}
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </FormItem>
            );
          }}
        />
        <FormField
          control={form.control}
          name='eval_config.additional_instructions'
          render={({ field }) => (
            <FormItem>
              <div className='flex items-center gap-2'>
                <FormLabel>Additional Instructions</FormLabel>
                <HelpTooltip label='Additional instructions help'>
                  Include any extra guidance that doesn’t fit into other fields.
                </HelpTooltip>
              </div>
              <FormControl>
                <Textarea
                  className='min-h-[100px]'
                  disabled={!isEditing}
                  {...field}
                  value={field.value ?? ''}
                  onChange={(e) => field.onChange(e.target.value)}
                  placeholder='e.g. include judging notes, edge cases, or style preferences'
                />
              </FormControl>
            </FormItem>
          )}
        />
      </>
    );
  }

  return (
    <BaseEvalConfig
      evalData={evalData}
      onSave={onSave}
      onDuplicate={onDuplicate}
      onDelete={onDelete}
      defaultValues={defaultValues}
      title='Score Evaluation'
      description={metadata?.description}
      onSubmitTransform={onSubmitTransform}
      onGenerateFromDescription={handleGenerateFromDescription}
      validationSchema={scoreEvalFormSchema}
      metadata={metadata}
    >
      {(form, isEditing) => <RenderFields form={form} isEditing={isEditing} />}
    </BaseEvalConfig>
  );
}
